/*
 *  TTTech nerve-management-system
 *  Copyright(c) 2021. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */
import mqtt from 'mqtt';
import { v4 as uuidv4 } from 'uuid';
import topicGroups from './topics';
import { handleOfflineEvent, handleOnConnect } from './mqtt-connection-events-handler';

const SUBACK_SUCCESS_CODES = [0, 1, 2];
/**
 * Concat all topics from topicGroups methods to a single array
 * @returns {*[]}
 */
const concatAllTopics = () => Object.keys(topicGroups).reduce((all, group) => [...all, ...topicGroups[group]()], []);
/**
 * Filter topics that have according matchBy defined and map them to array of topics
 * @param topics - Collection of defined topics from topics.js file
 * @returns {*}
 */
const prepareTopicsForClient = (topics) => topics.filter((t) => t.matchBy).map((item) => item.topic);

/**
 * Handle messages received for subscribed topics
 * by matching received topic with matchBy or name param from collection of defined topics
 * from topics.js file and dispatch matching store action defined in same collection
 * @param topic - topic received from mqtt broker
 * @param msg - message received from mqtt broker
 */
const handleMessages = (topic, msg) => {
  const parsedMsg = JSON.parse(msg);
  const matchingTopic = concatAllTopics().find((t) => {
    // matching by the name of topic
    if (t.name && parsedMsg && parsedMsg.name === t.name) {
      return topic;
    }
    // matching by the matchBy prop
    return topic.includes(t.matchBy);
  });
  if (!matchingTopic) {
    return;
  }

  const topicParams = topic.split('/');
  if (parsedMsg.name === 'WorkloadCtrl') {
    parsedMsg.params.sessionId = `${topicParams[1]}-${topicParams[3]}`;
  }

  // Check if handler function is defined for the matched topic
  if (typeof matchingTopic.action !== 'function') {
    return;
  }
  matchingTopic.action(parsedMsg);
};

const generateId = () => uuidv4().substring(0, 8);

const getLocalStorageItemByName = (name) => {
  const item = localStorage.getItem(name);
  if (!item) {
    throw new Error(`mqtt: get ${name} => Not logged in!`);
  }
  return JSON.parse(item);
};

const getSessionId = () => {
  const session = getLocalStorageItemByName('session');
  return session.sessionId;
};

const getUsername = () => {
  const userDetails = getLocalStorageItemByName('userDetails');
  return userDetails.username;
};

const promises = {};

class Mqtt {
  /**
   * Mqtt.js client
   * @type {{}}
   */
  client = {};

  /**
   * Install method for Vue plugin
   * @param Vue - Vue instance
   */

  logoutTimeout = null;

  timeoutTime = 60000;

  /**
   * Connect to mqtt broker and declare mqtt events
   * @param sessionId - authorization token
   * @param brokerUrl - Url of the broker
   */
  // eslint-disable-next-line no-restricted-globals
  connect({ brokerUrl = `wss://${location.hostname}/wss` } = {}) {
    if (this.client && this.client.connected) {
      return;
    }

    try {
      this.client = mqtt.connect(brokerUrl, {
        username: getUsername(),
        password: 'foo',
        clientId: `w-${getSessionId()}-${generateId()}`,
        keepalive: 20, // seconds, 0 is the default, can be any positive number
        reconnectPeriod: 10 * 1000,
        protocolVersion: 5,
      });
    } catch (e) {
      // U Vue 3, koristi globalProperties.$log ili console.debug
      if (typeof window !== 'undefined' && window?.$log?.debug) {
        window.$log.debug(`Cannot connect to mqtt:${e.message || e}`);
      } else {
        console.debug(`Cannot connect to mqtt:${e.message || e}`);
      }
    }
    this.client.on('connect', handleOnConnect.bind(null, this));

    this.client.on('message', (topic, msg) => handleMessages(topic, msg.toString()));

    this.client.on('offline', () => {
      // Always handle offline event, even in Cypress for E2E testing
      handleOfflineEvent(this);
    });
  }

  /**
   * Pass topic group to subscribe to those topics and according params object
   * @param topicGroup
   * @param params
   * @returns {Promise<void>}
   */
  async subscribeTo(topicGroup = 'alwaysSubscribed', params = {}) {
    try {
      params.sessionId = getSessionId();
      if (this.client && this.client.subscribe) {
        const result = await this.client.subscribe(prepareTopicsForClient(topicGroups[topicGroup](params)));
        // If the subscription fails - log the topic and error code
        if (Array.isArray(result) && result.length) {
          result.forEach(({ topic, qos }) => {
            if (!SUBACK_SUCCESS_CODES.includes(qos)) {
              if (typeof window !== 'undefined' && window?.$log?.debug) {
                window.$log.debug(`Failed to subscribe to '${topic}' - not authorized (${qos})`);
              } else {
                console.debug(`Failed to subscribe to '${topic}' - not authorized (${qos})`);
              }
            }
          });
        }
      }
    } catch (e) {
      if (typeof window !== 'undefined' && window?.$log?.debug) {
        window.$log.debug(e.message || e);
      } else {
        console.debug(e.message || e);
      }
    }
  }

  /**
   * Pass topic group to unsubscribe to those topics and according params object
   * @param topicGroup
   * @param params
   * @returns {Promise<void>}
   */
  async unsubscribeFrom(topicGroup = 'alwaysSubscribed', params = null) {
    try {
      if (this.client && this.client.connected) {
        await this.client.unsubscribe(prepareTopicsForClient(topicGroups[topicGroup](params)));
      }
    } catch (e) {
      if (typeof window !== 'undefined' && window?.$log?.debug) {
        window.$log.debug(e.message || e);
      } else {
        console.debug(e.message || e);
      }
    }
  }

  // eslint-disable-next-line consistent-return
  async publish({ topic, params }) {
    try {
      params.uid = generateId();
      params.sender = `cli/${getSessionId()}`;
      const promise = new Promise((resolve, reject) => {
        promises[params.uid] = { resolve, reject };
      });
      this.client.publish(topic, JSON.stringify(params), { qos: 0 }, (err) => {
        if (err) {
          if (typeof window !== 'undefined' && window?.$log?.debug) {
            window.$log.debug(`mqtt:publish:${err.message || err}`);
          } else {
            console.debug(`mqtt:publish:${err.message || err}`);
          }
        }
      });
      return promise;
    } catch (e) {
      if (typeof window !== 'undefined' && window?.$log?.debug) {
        window.$log.debug(`mqtt:publish:${e.message || e}`);
      } else {
        console.debug(`mqtt:publish:${e.message || e}`);
      }
    }
  }

  publishToNode(node, commandParams) {
    const topic = `oblo/${node.userId}/gtw/${node.serialNumber}/ohm/req`;
    return this.publish({ topic, params: commandParams });
  }

  publishToService({ service, params, type }) {
    const topic = `oblo/svc/${service}/${type}`;
    this.publish({ topic, params });
  }

  async disconnect() {
    if (this.client && typeof this.client.end === 'function') {
      await this.client.end(false);
    }
    this.client = null;
  }

  isConnected() {
    return this.client && this.client.connected;
  }
}

const mqttInstance = new Mqtt();

if (window.Cypress) {
  window.vmqtt = mqttInstance;
}

export default mqttInstance;
